/**
 * Link Outlook Events: which diary events in a period are linked to a workspace, and to what.
 * Dates and times are managed on job cards and installation bookings, never here.
 */

export type DiaryEvent = {
  id: string;
  subject: string;
  /** UK wall-clock times (fetched with the Europe/London preference). */
  start: { dateTime: string; timeZone: string };
  end: { dateTime: string; timeZone: string };
  isAllDay?: boolean;
  location?: { displayName: string };
  categories: string[];
  bodyPreview?: string;
  /** singleInstance, occurrence, exception or seriesMaster. */
  type?: string;
};

export type DiaryLink = {
  eventId: string;
  projectId: string;
  workspace: string;
  /** What the event is for, e.g. "Installation" or "Job card 2". */
  label: string;
};

export const EVENT_TYPE_LABELS: Record<string, string> = {
  installationDateConfirmation: 'Installation',
  deliveryDateConfirmation: 'Delivery',
  firstSurveyAppointmentConfirmation: 'First survey',
  finalSurveyAppointmentConfirmation: 'Final survey',
  otherKeystoneServices: 'Other',
};

export type DiaryRow = { event: DiaryEvent; links: DiaryLink[]; recurring: boolean };

/** Events in start order with their links. Unlinked-only hides linked events and recurring series. */
export function diaryRows(events: DiaryEvent[], links: DiaryLink[], unlinkedOnly: boolean): DiaryRow[] {
  const byEvent = new Map<string, DiaryLink[]>();
  links.forEach((link) => byEvent.set(link.eventId, [...(byEvent.get(link.eventId) || []), link]));
  return events
    .map((event) => ({
      event,
      links: byEvent.get(event.id) || [],
      recurring: !!event.type && event.type !== 'singleInstance',
    }))
    .filter((row) => !unlinkedOnly || (row.links.length === 0 && !row.recurring))
    .sort((a, b) => a.event.start.dateTime.localeCompare(b.event.start.dateTime));
}

function shortDay(day: string): string {
  return new Date(`${day}T12:00:00Z`).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC',
  });
}

function previousDay(day: string): string {
  const date = new Date(`${day}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().slice(0, 10);
}

/** "Mon 26 Oct – Wed 28 Oct · all day" or "Tue 27 Oct, 09:00–10:30". */
export function diaryWhen(event: Pick<DiaryEvent, 'start' | 'end' | 'isAllDay'>): string {
  const startDay = event.start.dateTime.slice(0, 10);
  const endDay = event.end.dateTime.slice(0, 10);
  if (event.isAllDay) {
    const lastDay = previousDay(endDay) < startDay ? startDay : previousDay(endDay);
    return `${shortDay(startDay)}${lastDay > startDay ? ` – ${shortDay(lastDay)}` : ''} · all day`;
  }
  const startTime = event.start.dateTime.slice(11, 16);
  const endTime = event.end.dateTime.slice(11, 16);
  if (endDay > startDay) return `${shortDay(startDay)}, ${startTime} – ${shortDay(endDay)}, ${endTime}`;
  return `${shortDay(startDay)}, ${startTime}–${endTime}`;
}

/** Monday (YYYY-MM-DD) of the week containing the day. */
export function mondayOf(day: string): string {
  const date = new Date(`${day}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7));
  return date.toISOString().slice(0, 10);
}

export function addDays(day: string, days: number): string {
  const date = new Date(`${day}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}
